import React, { useState, useEffect, useCallback } from 'react';
import { useCluster } from '../context/ClusterContext';
import { useNavigate } from 'react-router-dom';
import { clustersAPI } from '../services/api';
import { eventMatches } from '../utils/qaFixes';
import './ResourcePage.css';

export default function EventsPage() {
  const { selectedCluster } = useCluster();
  const navigate = useNavigate();
  const [events, setEvents]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [nsFilter, setNsFilter]   = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [total, setTotal]         = useState(0);

  const load = useCallback(async () => {
    if (!selectedCluster?.is_imported) return;
    setLoading(true); setError('');
    try {
      const params = { limit: 200 };
      if (typeFilter) params.event_type = typeFilter;
      const res = await clustersAPI.getEvents(selectedCluster.id, params);
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  // Primitives, not the whole object — see Dashboard.js/Deployments.js for why
  // (AuthContext's 30s permissions-refresh gives selectedCluster a new reference even
  // when nothing changed, which would otherwise reload this page every 30s).
  }, [selectedCluster?.id, selectedCluster?.is_imported, typeFilter]);

  useEffect(() => { load(); }, [load]);

  if (!selectedCluster) return (
    <div className="resource-page">
      <div className="page-header"><h1>Events</h1></div>
      <div className="no-cluster-msg">No cluster selected. <span className="link" onClick={() => navigate('/clusters')}>Select a cluster</span></div>
    </div>
  );

  // eventMatches (src/utils/qaFixes.js) matches the object/message filter on
  // object_kind + object_name + message (the Object column shows kind/name) and
  // is null-safe. Extracted so the fix is regression-tested (qaFixes.test.js).
  const filtered = events.filter(e => eventMatches(e, nsFilter, nameFilter));

  const warnings = filtered.filter(e => e.type === 'Warning').length;

  function timeAgo(iso) {
    if (!iso) return '';
    const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (diff < 60)   return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  }

  return (
    <div className="resource-page">
      <div className="page-header">
        <div>
          <h1>Events</h1>
          <div className="resource-cluster-badge">{selectedCluster.name}</div>
        </div>
        <button className="btn btn-secondary" onClick={load} disabled={loading}>
          {loading ? '⟳ Loading...' : '⟳ Refresh'}
        </button>
      </div>

      {error && <div className="resource-error">{error}</div>}

      {/* Summary badges */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <div style={{ background: 'var(--color-danger-50)', border: '1px solid var(--color-danger-50)', borderRadius: 8, padding: '8px 16px', fontSize: 13 }}>
          <span style={{ color: 'var(--color-danger-500)', fontWeight: 700 }}>{warnings}</span>
          <span style={{ color: 'var(--muted)', marginLeft: 6 }}>Warnings</span>
        </div>
        <div style={{ background: 'var(--color-success-50)', border: '1px solid var(--color-success-50)', borderRadius: 8, padding: '8px 16px', fontSize: 13 }}>
          <span style={{ color: 'var(--color-success-500)', fontWeight: 700 }}>{filtered.length - warnings}</span>
          <span style={{ color: 'var(--muted)', marginLeft: 6 }}>Normal</span>
        </div>
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', fontSize: 13 }}>
          <span style={{ color: 'var(--ink-2)', fontWeight: 700 }}>{total}</span>
          <span style={{ color: 'var(--muted)', marginLeft: 6 }}>Total fetched</span>
        </div>
      </div>

      {loading ? (
        <div className="resource-loading">Loading events...</div>
      ) : (
        <>
          <div className="resource-filter">
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="">All types</option>
              <option value="Warning">Warning</option>
              <option value="Normal">Normal</option>
            </select>
            <input placeholder="Filter namespace..." value={nsFilter}
              onChange={e => setNsFilter(e.target.value)} />
            <input placeholder="Filter object/message..." value={nameFilter}
              onChange={e => setNameFilter(e.target.value)} />
            <span className="resource-count">{filtered.length} events</span>
          </div>

          <div className="table-card">
            <table className="ds">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Namespace</th>
                  <th>Object</th>
                  <th>Reason</th>
                  <th>Message</th>
                  <th>Count</th>
                  <th>Age</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No events</td></tr>
                ) : filtered.map((e, i) => (
                  <tr key={i} style={{ background: e.type === 'Warning' ? 'var(--color-danger-50)' : undefined }}>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        background: e.type === 'Warning' ? 'var(--color-danger-50)' : 'var(--color-success-50)',
                        color: e.type === 'Warning' ? 'var(--color-danger-500)' : 'var(--color-success-500)',
                      }}>
                        {e.type}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{e.namespace}</td>
                    <td style={{ fontSize: 12 }}>
                      <span style={{ color: 'var(--muted)', fontSize: 11 }}>{e.object_kind}/</span>
                      <code style={{ fontSize: 12 }}>{e.object_name}</code>
                    </td>
                    <td style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-2)' }}>{e.reason}</td>
                    <td style={{ fontSize: 12, color: 'var(--ink-2)', maxWidth: 350 }}>
                      <span title={e.message}>{e.message.length > 120 ? e.message.slice(0, 120) + '…' : e.message}</span>
                    </td>
                    <td style={{ fontSize: 12, textAlign: 'center', color: e.count > 1 ? 'var(--color-danger-500)' : 'var(--muted)' }}>
                      {e.count > 1 ? <strong>×{e.count}</strong> : e.count}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--muted-2)', whiteSpace: 'nowrap' }}>{timeAgo(e.last_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
